import { numberWithCommas } from 'shared/utils/numberUtils';
import {
  IndicatorSectionKey,
  indicatorSectionsInOrder,
} from '../../ImpactIndicator/utils';
import { IndicatorDetails } from '../utils';
import { MEASURE_IDS } from '../../../constants';

export const getForecastNumber = (forecast: IndicatorDetails['forecast']) => {
  return forecast ? numberWithCommas(forecast) : 0;
};

export const indicatorAssumptionQuestionText: {
  [key in IndicatorSectionKey]: {
    [x: string]:
      | {
          heading: string;
          subheading: string;
        }
      | undefined;
  };
} = {
  capacityDevelopment: {
    'Staff or administrative project monitoring': {
      heading: 'Please describe your administrative measurement approach.',
      subheading:
        'If you have written agreements with partners, describe what you collect from whom.',
    },
    'Past program tracking': {
      heading: 'Please describe your previous approach to program tracking.',
      subheading:
        'If you have evidence from a similar program or research, describe and link to the measurement approach and detail your ' +
          'extrapolation to your own program.',
    },
    'Improvements documented in similar programs': {
      heading: 'Please reference studies or programs, and data used.',
      subheading:
        'If you have evidence from a similar program or research, describe and link to the measurement approach and detail your ' +
          'extrapolation to your own program.',
    },
    'Logical assumptions': {
      heading: 'Please describe your logical assumptions.',
      subheading:
        'If you expect that staff will improve their professional effectiveness as a result of resources you develop and post on ' +
          'your website, describe how you extrapolate the number of people trained from the number of resources downloaded.',
    },
    'Personal experience or anecdotal results': {
      heading: 'Please describe how your experience supports this metric.',
      subheading:
        'If you expect that staff will improve their professional effectiveness as a result of resources you develop and post on ' +
          'your website, describe how you extrapolate the number of people trained from the number of resources downloaded.',
    },
  },
  reach: {
    'Participant sign-in, attendance lists, or usage logs': {
      heading: 'Please describe your participation tracking approach.',
      subheading:
        'If you use a sign-in process, describe when and how participation data is collected.',
    },
    'Tracked data sources such as actual sales, member lists, etc.': {
      heading: 'Please describe the data sources used.',
      subheading:
        'If you are using a membership list, describe how that data is collected.',
    },
    'Tracked for this program in the past (but not currently measured)': {
      heading:
        'Please describe your previous approach to participant tracking.',
      subheading:
        'If you have evidence from a similar program or research, describe and link to the measurement approach and detail your ' +
          'extrapolation to your own program.',
    },
    'Published studies or data from similar programs': {
      heading:
        'Please reference studies or programs, and data used to measure participation.',
      subheading:
        'If you have evidence from a similar program or research, describe and link to the measurement approach and detail your ' +
          'extrapolation to your own program.',
    },
    'Logical assumptions': {
      heading: 'Please describe your assumptions.',
      subheading:
        'If you expect that people will participate as a result of your support to other organizations, describe how you extrapolate ' +
          'the number of people reached from the resources you provide.',
    },
    'Personal experience or anecdotal results': {
      heading: 'Please describe how your experience supports this metric.',
      subheading:
        'If you have limited tracking of participant engagement, describe how you extrapolate the number of people reached through ' +
          'your anecdotal experience.',
    },
  },
  learn: {
    'People\'s self-reported learning': {
      heading:
        'Please describe your collection approach for people\'s self-reported learning.',
      subheading:
        'If you use surveys to gather information from participants, describe what questions you ask to measure learning gains.',
    },
    'A formal assessment of learning gains': {
      heading: 'Please describe your collection approach for learning gains.',
      subheading:
        'If you use a test to measure learning gains, describe what questions you ask and how it is collected.',
    },
    'Past program learning gains (but not currently measured)': {
      heading:
        'Please describe your previous approach to tracking learning gains.',
      subheading:
        'If you have evidence from a similar program or research, describe and link to the measurement approach and detail your ' +
          'extrapolation to your own program.',
    },
    'Published studies or data from similar programs with documented learning gains': {
      heading:
        'Please reference studies or programs, and data used to measure learning gains.',
      subheading:
        'If you have evidence from a similar program or research, describe and link to the measurement approach and detail your ' +
          'extrapolation to your own program.',
    },
    'Logical assumptions': {
      heading: 'Please describe your assumptions.',
      subheading:
        'If you expect participants to gain skills or knowledge as a result of participation, describe why you expect people to ' +
          'learn and detail your assumptions.',
    },
    'Personal experience or anecdotal results': {
      heading: 'Please describe how your experience supports this metric.',
      subheading:
        'If you have limited tracking of learning gains, describe how you extrapolate the number of people who gained skills through ' +
          'your anecdotal experience.',
    },
  },
  act: {
    'People\'s self-reported behavior change': {
      heading:
        'Please describe your collection approach for people\'s self-reported behavior change.',
      subheading:
        'If you use surveys to gather information from participants, describe what questions you ask to measure behavior change.',
    },
    'A formal assessment of behavior change': {
      heading: 'Please describe your collection approach for behavior change.',
      subheading:
        'If you use a test to measure behavior change, describe what questions you ask and how it is collected.',
    },
    'People\'s expected behavior change': {
      heading:
        'Please describe your collection approach for expected behavior change.',
      subheading:
        'If you use surveys to gather information, describe what questions you ask to measure participant expectations of behavior change.',
    },
    'Past program behavior change (but not currently measured)': {
      heading:
        'Please describe your previous approach to tracking behavior change. ',
      subheading:
        'If you have evidence from a similar program or research, describe and link to the measurement approach and detail your ' +
          'extrapolation to your own program.',
    },
    'Published studies or data from similar programs with documented behavior change': {
      heading:
        'Please reference studies or programs, and data used to measure behavior change.',
      subheading:
        'If you have evidence from a similar program or research, describe and link to the measurement approach and detail your ' +
          'extrapolation to your own program.',
    },
    'Logical assumptions': {
      heading: 'Please describe your assumptions.',
      subheading:
        'If you expect participants to take action as a result of participation, describe why you expect people to change their ' +
          'behavior and detail your assumptions',
    },
    'Personal experience or anecdotal results': {
      heading: 'Please describe how your experience supports this metric.',
      subheading:
        'If you have limited tracking of behavior change, describe how you extrapolate the number of people who take action through ' +
          'your anecdotal experience.',
    },
  },
  succeed: {
    'People\'s own opinion of social impact': {
      heading:
        'Please describe your collection approach for people\'s opinion of social impact.',
      subheading:
        'If you use surveys to gather information from participants, describe what questions you ask to measure social impact.',
    },
    'A formal assessment of social impact': {
      heading: 'Please describe your collection approach for social impact.',
      subheading:
        'If you use a test or set of criteria to track social impact, describe what questions you ask and how it is collected.',
    },
    'Past program social impact (but not currently measured)': {
      heading:
        'Please describe your previous approach to tracking social impact.',
      subheading:
        'If you have evidence from a similar program or research, describe and link to the measurement approach and detail your ' +
          'extrapolation to your own program.',
    },
    'Published studies or data from similar programs with documented social impact': {
      heading:
        'Please reference studies or programs, and data used to measure social impact.',
      subheading:
        'If you have evidence from a similar program or research, describe and link to the measurement approach and detail your ' +
          'extrapolation to your own program.',
    },
    'Logical assumptions': {
      heading: 'Please describe your assumptions.',
      subheading:
        'If you expect social outcomes from this program, describe why and detail your assumptions',
    },
    'Personal experience or anecdotal results': {
      heading: 'Please describe how your experience supports this metric.',
      subheading:
        'If you have limited tracking of social impact, describe how you extrapolate the number of impacts acheived through your ' +
          'anecdotal experience.',
    },
  },
  catalyzed: {},
};

const defaultHeading =
  'What sources or assumptions do you use to forecast your results?';
export const getIndicatorAssumptionQuestionText = (
  sectionKey: IndicatorSectionKey,
  measurementAnswer: string | undefined,
): { heading: string; subheading?: string } => {
  if (!measurementAnswer) return { heading: defaultHeading };
  let result = indicatorAssumptionQuestionText[sectionKey][measurementAnswer];
  if (!result) result = {} as { heading: string; subheading: string };
  if (!result.heading) result.heading = defaultHeading;
  return result;
};

export type IndicatorDetailsMeasurementType =
  | 'Direct Measurement'
  | 'Estimate Based on Data'
  | 'Guess';

const indicatorDetailsMeasurementText: {
  [key in IndicatorSectionKey]: {
    [type in IndicatorDetailsMeasurementType]: string[];
  };
} = {
  capacityDevelopment: {
    Guess: ['Logical assumptions', 'Personal experience or anecdotal results'],
    'Direct Measurement': ['Staff or administrative project monitoring'],
    'Estimate Based on Data': [
      'Past program tracking',
      'Improvements documented in similar programs',
    ],
  },
  act: {
    Guess: ['Logical assumptions', 'Personal experience or anecdotal results'],
    'Direct Measurement': [
      'People\'s self-reported behavior change',
      'A formal assessment of behavior change',
      'People\'s expected behavior change',
    ],
    'Estimate Based on Data': [
      'Past program behavior change (but not currently measured)',
      'Published studies or data from similar programs with documented behavior change',
    ],
  },
  catalyzed: {
    Guess: [],
    'Direct Measurement': [],
    'Estimate Based on Data': [],
  },
  learn: {
    Guess: ['Logical assumptions', 'Personal experience or anecdotal results'],
    'Direct Measurement': [
      'People\'s self-reported learning',
      'A formal assessment of learning gains',
    ],
    'Estimate Based on Data': [
      'Past program learning gains (but not currently measured)',
      'Published studies or data from similar programs with documented learning gains',
    ],
  },
  reach: {
    Guess: ['Logical assumptions', 'Personal experience or anecdotal results'],
    'Direct Measurement': [
      'Participant sign-in, attendance lists, or usage logs',
      'Tracked data sources such as actual sales, member lists, etc.',
    ],
    'Estimate Based on Data': [
      'Tracked for this program in the past (but not currently measured)',
      'Published studies or data from similar programs',
    ],
  },
  succeed: {
    Guess: ['Logical assumptions', 'Personal experience or anecdotal results'],
    'Direct Measurement': [
      'People\'s own opinion of social impact',
      'A formal assessment of social impact',
    ],
    'Estimate Based on Data': [
      'Past program social impact (but not currently measured)',
      'Published studies or data from similar programs with documented social impact',
    ],
  },
};

const measurementTypes: IndicatorDetailsMeasurementType[] = [
  'Direct Measurement',
  'Estimate Based on Data',
  'Guess',
];

const measurementTypeMap: Record<IndicatorDetailsMeasurementType, string> = {
  'Direct Measurement': 'Direct:',
  'Estimate Based on Data': 'Estimate:',
  Guess: 'Guess:',
};

export const getIndicatorDetailsMeasurementType = (
  measurement: string,
): string | undefined => {
  for (const key of indicatorSectionsInOrder) {
    for (const type of measurementTypes) {
      if (indicatorDetailsMeasurementText[key][type].includes(measurement)) {
        return measurementTypeMap[type];
      }
    }
  }
  return undefined;
};

export const getIndicatorDetailsMeasurement = (
  sectionKey: IndicatorSectionKey,
  type?: IndicatorDetailsMeasurementType,
): string[] => {
  if (type) {
    return indicatorDetailsMeasurementText[sectionKey][type];
  }
  return indicatorDetailsMeasurementText[sectionKey]['Direct Measurement']
    .concat(
      indicatorDetailsMeasurementText[sectionKey]['Estimate Based on Data'],
    )
    .concat(indicatorDetailsMeasurementText[sectionKey]['Guess']);
};

export const getIndicatorDetailsMeasurementKey = (
  sectionKey: IndicatorSectionKey,
  measurement?: string,
): IndicatorDetailsMeasurementType | undefined => {
  if (!measurement) return;

  const direct =
    indicatorDetailsMeasurementText[sectionKey]['Direct Measurement'];
  const estimate =
    indicatorDetailsMeasurementText[sectionKey]['Estimate Based on Data'];
  const guess = indicatorDetailsMeasurementText[sectionKey]['Guess'];

  const directIndex = direct.findIndex(x => x === measurement);
  if (directIndex !== -1) return 'Direct Measurement';

  const estimateIndex = estimate.findIndex(x => x === measurement);
  if (estimateIndex !== -1) return 'Estimate Based on Data';

  const guessIndex = guess.findIndex(x => x === measurement);
  if (guessIndex !== -1) return 'Guess';
};

export const getLegacyIndicatorDetailsMeasurementKey = (
  measurement?: string,
): {
  isLegacy: boolean;
  measurement: IndicatorDetailsMeasurementType | undefined;
} => {
  if (!measurement || !MEASURE_IDS[measurement as keyof typeof MEASURE_IDS]) {
    return { isLegacy: false, measurement: undefined };
  }
  switch (measurement[0]) {
    case 'M':
      return { isLegacy: true, measurement: 'Direct Measurement' };
    case 'E':
      return { isLegacy: true, measurement: 'Estimate Based on Data' };
    case 'G':
      return { isLegacy: true, measurement: 'Guess' };
    default:
      return { isLegacy: false, measurement: undefined };
  }
};

export const getReachIndicator = (
  reach: IndicatorDetails[] | undefined,
): IndicatorDetails | undefined => {
  if (!reach) return undefined;
  return reach.find(x => x.isHuman) || reach[0];
};
